import axios from 'axios';

// Always use relative /api path for same-origin requests
// This works in both preview (proxied) and production (same domain)
const API = '/api';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Public APIs
export const publicAPI = {
  getCities: () => api.get('/public/cities'),
  getCityHub: (citySlug) => api.get(`/public/city-hub/${citySlug}`),
  getServices: () => api.get('/public/services'),
  getLanding: (citySlug, serviceSlug) => api.get(`/public/landing/${citySlug}/${serviceSlug}`),
  getBlog: (page = 1) => api.get(`/public/blog?page=${page}`),
  getBlogPost: (slug) => api.get(`/public/blog/${slug}`),
  getContent: (pageKey) => api.get(`/public/content/${pageKey}`),
  getPublicSettings: () => api.get('/public/settings'),
  getPublicBranding: () => api.get('/public/branding'),
  getAllLandings: () => api.get('/public/all-landings'),
  submitQuote: (formData) => api.post('/public/quote', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Directory
  getDirectoryCategories: () => api.get('/public/directory/categories'),
  getDirectoryCategory: (slug, page) => api.get(`/public/directory/category/${slug}?page=${page || 1}`),
  getDirectoryCompany: (slug) => api.get(`/public/directory/company/${slug}`),
};

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  checkSetup: () => api.get('/auth/check-setup'),
  setup: (data) => api.post('/auth/setup', data),
};

// Admin APIs
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  // Leads
  getLeads: (params) => api.get('/admin/leads', { params }),
  getLead: (id) => api.get(`/admin/leads/${id}`),
  updateLeadStatus: (id, status) => api.put(`/admin/leads/${id}/status`, { status }),
  addLeadNote: (id, text) => api.post(`/admin/leads/${id}/notes`, { text }),
  assignLead: (id, data) => api.post(`/admin/leads/${id}/assign`, data),
  // Companies
  getCompanies: () => api.get('/admin/companies'),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  // Services
  getServices: () => api.get('/admin/services'),
  createService: (data) => api.post('/admin/services', data),
  updateService: (id, data) => api.put(`/admin/services/${id}`, data),
  deleteService: (id) => api.delete(`/admin/services/${id}`),
  // Cities
  getCities: (search) => api.get('/admin/cities', { params: { search } }),
  updateCity: (id, data) => api.put(`/admin/cities/${id}`, data),
  // Landing Pages
  getLandingPages: () => api.get('/admin/landing-pages'),
  getLandingStats: () => api.get('/admin/landing-pages/stats'),
  bulkPublishCheck: (count) => api.post('/admin/landing-pages/bulk-publish-check', { count }),
  createLandingPage: (data) => api.post('/admin/landing-pages', data),
  updateLandingPage: (id, data) => api.put(`/admin/landing-pages/${id}`, data),
  deleteLandingPage: (id) => api.delete(`/admin/landing-pages/${id}`),
  // Blog
  getBlog: () => api.get('/admin/blog'),
  createBlog: (data) => api.post('/admin/blog', data),
  updateBlog: (id, data) => api.put(`/admin/blog/${id}`, data),
  deleteBlog: (id) => api.delete(`/admin/blog/${id}`),
  // Content
  getContent: () => api.get('/admin/content'),
  updateContent: (blocks) => api.put('/admin/content', { blocks }),
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  // Branding
  getBranding: () => api.get('/admin/branding'),
  uploadFavicon: (formData) => api.post('/admin/branding/favicon', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadLogo: (formData) => api.post('/admin/branding/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Media
  getMedia: () => api.get('/admin/media'),
  uploadMedia: (formData) => api.post('/admin/media', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteMedia: (id) => api.delete(`/admin/media/${id}`),
  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  // Sitemap
  getSitemapStatus: () => api.get('/admin/sitemap-status'),
  pingSitemap: () => api.post('/admin/sitemap-ping'),
  // Users
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  // File download
  downloadFile: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
  // Content Templates
  getTemplates: () => api.get('/admin/templates'),
  createTemplate: (data) => api.post('/admin/templates', data),
  updateTemplate: (id, data) => api.put(`/admin/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/admin/templates/${id}`),
  checkDuplicate: (text) => api.post('/admin/content-check-duplicate', { text }),
  // Directory Admin
  getDirCategories: () => api.get('/admin/directory/categories'),
  createDirCategory: (data) => api.post('/admin/directory/categories', data),
  updateDirCategory: (id, data) => api.put(`/admin/directory/categories/${id}`, data),
  getDirCompanies: (categoryId) => api.get('/admin/directory/companies', { params: { category_id: categoryId } }),
  getDirArticles: (catId) => api.get(`/admin/directory/articles/${catId}`),
  createDirCompany: (data) => api.post('/admin/directory/companies', data),
  updateDirCompany: (id, data) => api.put(`/admin/directory/companies/${id}`, data),
  deleteDirCompany: (id) => api.delete(`/admin/directory/companies/${id}`),
};

export default api;
